import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
let WordCloud;

const colors = ["#3E3E3E", "#666666", "#E59536", "#BE6A03", "#EEB52B", "#F28907"];

const getRandomColor = () => {
  return colors[Math.floor(Math.random() * colors.length)];
};

export default function WordCloudRenderer({
  id,
  type = "canvas",
  words = [],
  width = 1500,
  height = 1500,
  dynamicHeightFactor = 1,
  fontFamily = "Arial, sans-serif",
  backgroundColor = "#fff",
  border = "",
  fontWeight = "bold",
  gridSize = 20,
  weightFactorFormula = (size, totalWords) => size * (totalWords <= 10 ? 80 : 40 + width / height),
  colorFunction = getRandomColor,
  shrinkToFit = true,
  drawOutOfBound = false,
  wordClickCallback = () => {},
  customClassesCallback = () => {},
  isPdf = false,
  canvasWidth = "100%",
  canvasHeight = "70vh",
  showEmpty = false
}) {
  const canvasRef = useRef(null);
  const { t } = useTranslation();

  const renderEmptyPlaceholder = () => {
    if (!canvasRef.current || type !== "canvas") return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Get the DPI and size
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    // Set the canvas buffer size to match display size * device pixel ratio
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    // Scale the context to ensure correct drawing operations
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Set background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Add placeholder text with improved resolution
    ctx.fillStyle = "#9CA3AF";
    ctx.font = `${fontWeight} 24px ${fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(t("wordCloud.noWords"), rect.width / 2, rect.height / 2);
  };

  const renderWordCloud = (wordsList) => {
    if (!canvasRef.current) return;

    if (wordsList.length === 0) {
      if (showEmpty) {
        renderEmptyPlaceholder();
      }
      return;
    }

    const canvas = canvasRef.current;
    canvas.setAttribute("width", width);
    const canvasPixelWidth = canvas.clientWidth;
    const canvasPixelHeight = canvas.clientHeight;
    const ratio = canvasPixelHeight / canvasPixelWidth;

    canvas.setAttribute("height", width * (ratio || 1) * dynamicHeightFactor);

    WordCloud(canvas, {
      list: wordsList,
      fontFamily,
      backgroundColor,
      fontWeight,
      gridSize,
      shrinkToFit,
      drawOutOfBound,
      clearCanvas: true,
      rotateRatio: 0.5,
      rotationSteps: 2,
      weightFactor: function (size) {
        return weightFactorFormula(size, wordsList.length, width, height);
      },
      color: function (word, weight) {
        return colorFunction(word, weight);
      },
      click: function (item) {
        wordClickCallback(item[0]);
      },
      classes: function (word) {
        return customClassesCallback(word);
      }
    });
  };

  useEffect(() => {
    if (!canvasRef?.current || typeof window === "undefined") return;

    try {
      const canvasWidth = canvasRef.current.clientWidth;
      canvasRef.current.style.height = isPdf ? "60vh" : `${canvasWidth * dynamicHeightFactor}px`;
    } catch (error) {
      console.log("unable to set height");
    }

    (async () => {
      if (!WordCloud) WordCloud = (await import("wordcloud")).default;
      renderWordCloud(words);
    })();

    return () => {
      WordCloud?.stop && WordCloud.stop(canvasRef.current);
    };
  }, [id, words, WordCloud, showEmpty]);

  if (!words?.length && !showEmpty) return null;

  return (
    <div
      id={id}
      className="px-2 py-4 border-2 border-gray-250/40 rounded-lg overflow-hidden"
      style={{
        background: backgroundColor,
        width: type === "html" && "100%",
        border: border && border
      }}>
      {type === "canvas" && (
        <canvas
          ref={canvasRef}
          style={{
            display: "block",
            width: canvasWidth,
            height: canvasHeight,
            margin: "auto",
            minHeight: showEmpty ? "300px" : undefined
          }}
        />
      )}
      {type === "html" && (
        <div
          ref={canvasRef}
          style={{
            display: "block",
            width: "100%",
            height: "70vh",
            fontFamily,
            fontWeight,
            minHeight: showEmpty ? "300px" : undefined
          }}
        />
      )}
    </div>
  );
}
